import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCheck, Mail, Phone, ShieldCheck, Trash2, Send, ArrowLeft } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Nominee appointment settings — India users must appoint a family
 * nominee under DPDP 2023 so their family archive is inherited if they
 * pass away. Non-India users see the same page but with a lighter
 * "recommended" nudge instead of a compliance requirement.
 */
export default function NomineeSettings() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [verifyingSend, setVerifyingSend] = useState(false);
  const [form, setForm] = useState({ name: "", relation: "", phone: "", email: "", note: "" });

  useEffect(() => {
    if (!authLoading && !user) nav("/login?redirect=/settings/nominee", { replace: true });
  }, [authLoading, user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await http.get("/me/nominee");
      setData(d);
      if (d.nominee) {
        setForm({
          name: d.nominee.name || "",
          relation: d.nominee.relation || "",
          phone: d.nominee.phone || "",
          email: d.nominee.email || "",
          note: d.nominee.note || "",
        });
      }
    } catch {
      toast.error("Couldn't load nominee. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const isIndia = data?.data_region === "in";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.relation) { toast.error("Name and relation are required."); return; }
    if (!form.phone && !form.email) { toast.error("Please add a phone number or an email so we can reach your nominee."); return; }
    setBusy(true);
    try {
      const { data: d } = await http.put("/me/nominee", form);
      toast.success("Nominee saved.");
      setData((prev) => ({ ...(prev || {}), nominee: d.nominee }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your appointed nominee? This will be logged in your account history.")) return;
    setBusy(true);
    try {
      await http.delete("/me/nominee");
      setData((prev) => ({ ...(prev || {}), nominee: null }));
      setForm({ name: "", relation: "", phone: "", email: "", note: "" });
      toast.success("Nominee removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendVerify = async () => {
    setVerifyingSend(true);
    try {
      const { data: d } = await http.post("/me/nominee/verify-send");
      if (d.email_delivered) {
        toast.success("Verification email sent to your nominee.");
      } else {
        toast.info("Nominee will be verified via out-of-band process. Ask admin to relay if needed.");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send verification.");
    } finally {
      setVerifyingSend(false);
    }
  };

  if (authLoading || loading) {
    return <div className="max-w-2xl mx-auto p-8 text-sm opacity-60">Loading…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="nominee-settings-page">
      <Helmet><title>{`Appoint a Nominee — Your Family Archive`}</title></Helmet>

      <button onClick={() => nav(-1)} className="wofy-btn-ghost text-xs mb-4" data-testid="nominee-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B58936" }}>
        {isIndia ? "India · DPDP 2023" : "Legacy Planning"}
      </div>
      <h1 className="font-display text-3xl leading-tight" style={{ color: "#3B2E1F" }}>
        Appoint a family nominee.
      </h1>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: "#6B5A3F" }}>
        Choose a trusted family member who inherits your family archive — trees, memorials, photos, stories — if you pass away.
        {isIndia && " India's Digital Personal Data Protection Act, 2023 requires every user to have this option."}
      </p>

      {data?.nominee && (
        <div
          className="mt-6 rounded-xl border p-4 flex items-start gap-3"
          style={{ background: "#FFF8EC", borderColor: "#FF993355" }}
          data-testid="nominee-current-card"
        >
          <UserCheck className="w-5 h-5 mt-0.5 flex-none" style={{ color: "#B58936" }} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold" style={{ color: "#3B2E1F" }}>
              {data.nominee.name}
              <span className="ml-2 text-xs font-normal opacity-70">· {data.nominee.relation}</span>
              {data.nominee.verified_at && (
                <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ color: "#5C6B52" }} title={`Verified on ${data.nominee.verified_at.slice(0,10)}`}>
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="mt-1 text-xs" style={{ color: "#6B5A3F" }}>
              {data.nominee.phone && <span><Phone className="inline w-3 h-3 mr-1" />{data.nominee.phone}</span>}
              {data.nominee.phone && data.nominee.email && <span className="mx-2">·</span>}
              {data.nominee.email && <span><Mail className="inline w-3 h-3 mr-1" />{data.nominee.email}</span>}
            </div>
            {data.nominee.note && (
              <div className="mt-1 text-xs italic" style={{ color: "#6B5A3F" }}>&ldquo;{data.nominee.note}&rdquo;</div>
            )}
            <div className="mt-3 flex gap-2 flex-wrap">
              {!data.nominee.verified_at && (
                <button
                  onClick={sendVerify}
                  disabled={verifyingSend}
                  className="wofy-btn-primary text-xs disabled:opacity-60"
                  data-testid="nominee-send-verify-btn"
                >
                  <Send className="w-3 h-3" /> {verifyingSend ? "Sending…" : "Send verification"}
                </button>
              )}
              <button
                onClick={remove}
                disabled={busy}
                className="wofy-btn-ghost text-xs disabled:opacity-60"
                style={{ color: "#B23A3A" }}
                data-testid="nominee-remove-btn"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="nominee-form">
        <div className="text-sm font-semibold" style={{ color: "#3B2E1F" }}>
          {data?.nominee ? "Update or replace" : "Nominee details"}
        </div>

        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Full name *</span>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full rounded-lg border px-3 py-2 bg-white text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            placeholder="e.g. Priya Menon"
            data-testid="nominee-name-input"
          />
        </label>

        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Relation *</span>
          <input
            type="text"
            required
            value={form.relation}
            onChange={(e) => setForm({ ...form, relation: e.target.value })}
            className="mt-1 w-full rounded-lg border px-3 py-2 bg-white text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            placeholder="e.g. Daughter, Son, Spouse, Sibling"
            data-testid="nominee-relation-input"
          />
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Phone (WhatsApp)</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-white text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="+91 98765 43210"
              data-testid="nominee-phone-input"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-white text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="priya@example.com"
              data-testid="nominee-email-input"
            />
          </label>
        </div>
        <p className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
          At least one of phone / email is required so we can reach your nominee.
        </p>

        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">Note to your nominee (optional)</span>
          <textarea
            rows={3}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="mt-1 w-full rounded-lg border px-3 py-2 bg-white text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            placeholder="Anything they should know if they ever need to inherit your archive."
            maxLength={500}
            data-testid="nominee-note-input"
          />
        </label>

        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary text-sm disabled:opacity-60"
          data-testid="nominee-save-btn"
        >
          {busy ? "Saving…" : (data?.nominee ? "Update nominee" : "Appoint nominee")}
        </button>
      </form>

      <div className="mt-10 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        <strong>What happens next?</strong> Once appointed, your nominee will receive a one-time verification link.
        We'll notify them if you ever pass away, and an admin will approve the transfer of your archive to them.
        You can change or remove your nominee at any time — all changes are logged in your account history.
      </div>
    </div>
  );
}
